import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, PieChart, Pie, Cell } from "recharts";
import { Activity, CalendarDays, Clock, AlertCircle, ChevronDown, PieChart as PieIcon } from "lucide-react";
import { GlassCard, fmtMoney } from "@/components/erp";
import api from "@/lib/api";

const DONEM = [1, 4, 8, 12];
const kisa = (v) => new Intl.NumberFormat("tr-TR", { notation: "compact", maximumFractionDigits: 1 }).format(v) + "₺";
const gunAy = (s) => { const [, m, d] = s.split("-"); return `${d}.${m}`; };
const tip = { contentStyle: { background: "#0f1220", border: "1px solid rgba(255,255,255,.1)", borderRadius: 12, fontSize: 12 } };

const NakitGrafik = ({ d, hafta, setHafta }) => (
  <GlassCard className="p-5 fade-up" data-testid="panel-nakit-grafik">
    <div className="flex flex-wrap items-center gap-3 mb-4">
      <Activity className="w-5 h-5 text-[var(--g1)]" />
      <h3 className="font-head font-semibold" data-testid="panel-nakit-baslik">Son {hafta} Haftalık Nakit Akışı</h3>
      <div className="ml-auto flex gap-1">{DONEM.map((h) => (
        <button key={h} data-testid={`panel-donem-${h}`} onClick={() => setHafta(h)}
          className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${hafta === h ? "grad-bg text-white" : "bg-white/5 text-muted-foreground hover:text-white"}`}>{h}H</button>
      ))}</div>
    </div>
    <div className="h-72">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={d?.seri || []} margin={{ left: 8, right: 12, top: 8 }}>
          <CartesianGrid stroke="rgba(255,255,255,.06)" vertical={false} />
          <XAxis dataKey="tarih" tickFormatter={gunAy} tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} />
          <YAxis tickFormatter={kisa} tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} width={64} />
          <Tooltip {...tip} labelFormatter={(l) => (d?.gunluk ? gunAy(l) : `${gunAy(l)} haftası`)} formatter={(v, n) => [fmtMoney(v), n]} />
          <Legend verticalAlign="top" height={28} wrapperStyle={{ fontSize: 12 }} />
          <Line type="monotone" dataKey="gelir" name="Gelir" stroke="#10b981" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
          <Line type="monotone" dataKey="gider" name="Gider" stroke="#ef4444" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </GlassCard>
);

const Halka = ({ baslik, o, testid, renk }) => {
  const veri = [{ ad: "Vadesi geçen", v: o?.gecikmis || 0, c: "#ef4444" }, { ad: "Vadesi gelmemiş", v: o?.bekleyen || 0, c: renk }];
  const bos = !o?.toplam;
  return (
    <div className="flex flex-col items-center" data-testid={testid}>
      <div className="font-semibold mb-2">{baslik}</div>
      <div className="relative w-52 h-52">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={bos ? [{ ad: "Kayıt yok", v: 1, c: "rgba(148,163,184,.22)" }] : veri} dataKey="v" nameKey="ad" innerRadius={68} outerRadius={92} stroke="none" startAngle={90} endAngle={-270}>
              {(bos ? [{ c: "rgba(148,163,184,.22)" }] : veri).map((x, i) => <Cell key={i} fill={x.c} />)}
            </Pie>
            {!bos && <Tooltip {...tip} formatter={(v, n) => [fmtMoney(v), n]} />}
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none font-mono font-semibold text-sm" data-testid={`${testid}-toplam`}>{fmtMoney(o?.toplam)}</div>
      </div>
      <div className="mt-3 space-y-1 text-xs w-full max-w-[220px]">{veri.map((x) => (
        <div key={x.ad} className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: x.c }} /><span className="text-muted-foreground">{x.ad}</span><span className="ml-auto font-mono">{fmtMoney(x.v)}</span></div>
      ))}</div>
    </div>
  );
};

const Ozetler = ({ d }) => (
  <GlassCard className="p-5 fade-up" data-testid="panel-ozetler">
    <div className="flex items-center gap-3 mb-5"><PieIcon className="w-5 h-5 text-[var(--g1)]" /><h3 className="font-head font-semibold">Tahsilat ve Ödeme Özetleri</h3></div>
    <div className="grid sm:grid-cols-2 gap-8">
      <Halka baslik="Tahsilatlar" o={d?.tahsilat} renk="#6366f1" testid="panel-tahsilat" />
      <Halka baslik="Ödemeler" o={d?.odeme} renk="#64748b" testid="panel-odeme" />
    </div>
  </GlassCard>
);

const IsGrubu = ({ k, baslik, icon: Icon, g, renk, acikBas }) => {
  const [acik, setAcik] = useState(acikBas);
  return (
    <div className="border-b border-white/5 last:border-0">
      <button data-testid={`panel-isler-${k}`} onClick={() => setAcik(!acik)} className="w-full flex items-center gap-3 py-3 text-sm hover:text-white transition-colors">
        <Icon className={`w-4 h-4 ${renk}`} /><span className="font-medium">{baslik}</span>
        <span className={`ml-auto min-w-6 px-1.5 py-0.5 rounded-md text-[11px] font-semibold bg-white/5 ${renk}`} data-testid={`panel-isler-${k}-adet`}>{g?.adet || 0}</span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${acik ? "rotate-180" : ""}`} />
      </button>
      {acik && (
        <div className="pb-3 space-y-1.5 max-h-64 overflow-y-auto erp-scroll" data-testid={`panel-isler-${k}-liste`}>
          {!g?.adet && <div className="text-xs text-muted-foreground px-1">Kayıt yok.</div>}
          {g?.liste.map((x, i) => (
            <Link key={i} to={x.yol} className="flex items-center gap-2 text-xs px-2 py-1.5 rounded-lg hover:bg-white/5">
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${x.yon === "giris" ? "bg-emerald-400" : "bg-red-400"}`} />
              <span className="min-w-0"><span className="block truncate">{x.aciklama}</span><span className="text-muted-foreground">{x.tur} · {gunAy(x.vade)}</span></span>
              <span className={`ml-auto font-mono shrink-0 ${x.yon === "giris" ? "text-emerald-300" : "text-red-300"}`}>{x.yon === "giris" ? "+" : "−"}{fmtMoney(x.tutar)}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const GunuGelen = ({ d }) => (
  <GlassCard className="p-5 fade-up self-start" data-testid="panel-gunu-gelen">
    <div className="flex items-center gap-3 mb-2"><CalendarDays className="w-5 h-5 text-[var(--g1)]" /><h3 className="font-head font-semibold">Günü Gelen İşlemler</h3></div>
    <IsGrubu k="bugun" baslik="Bugün" icon={CalendarDays} g={d?.isler.bugun} renk="text-sky-300" acikBas />
    <IsGrubu k="yaklasan" baslik="Yaklaşanlar (7 gün)" icon={Clock} g={d?.isler.yaklasan} renk="text-amber-300" />
    <IsGrubu k="gecmis" baslik="Tarihi Geçenler" icon={AlertCircle} g={d?.isler.gecmis} renk="text-red-300" />
  </GlassCard>
);

export const PanelGrafik = () => {
  const [hafta, setHafta] = useState(1);
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/panel/grafik", { params: { hafta } }).then((r) => setD(r.data)).catch(() => setD(null)); }, [hafta]);
  return (
    <div className="grid xl:grid-cols-[1fr_360px] gap-6 mb-8" data-testid="panel-grafik">
      <div className="space-y-6 min-w-0"><NakitGrafik d={d} hafta={hafta} setHafta={setHafta} /><Ozetler d={d} /></div>
      <GunuGelen d={d} />
    </div>
  );
};
